import { BRUSH_ACCEPT, IMAGE_ACCEPT, pickFiles } from '../io/import';
import {
  applyLayerEffectsPreset,
  openImageAsDocument,
  openPdfAsDocument,
  openPsdAsDocument,
  placeImageAsLayer,
} from './actions';
import { importBrushFile } from './brushActions';
import { parseAsl, parseStyleJson } from '../io/asl';
import { findLayerById } from '../core/layerTree';
import { useAppStore } from './store';

function isBrushFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.abr');
}

function isPsdFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.psd') || name.endsWith('.psb');
}

function isPdfFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.pdf') || file.type === 'application/pdf';
}

function isStyleFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.asl') || name.endsWith('.json');
}

/**
 * File > Open. Photopea accepts brush sets through the same entry as images, and
 * the extension is the only thing that tells them apart.
 */
export async function openFile(file: File): Promise<void> {
  if (isBrushFile(file)) {
    await importBrushFile(file);
    return;
  }
  if (isPsdFile(file)) {
    await openPsdAsDocument(file);
    return;
  }
  if (isPdfFile(file)) {
    await openPdfAsDocument(file);
    return;
  }
  if (isStyleFile(file)) {
    await importStyleFile(file);
    return;
  }
  await openImageAsDocument(file);
}

export async function openFilesFromPicker(): Promise<void> {
  const files = await pickFiles(
    `${IMAGE_ACCEPT},${BRUSH_ACCEPT},.psd,.psb,.pdf,application/pdf,.asl,application/json`,
    true,
  );
  for (const file of files) await openFile(file);
}

/** Drag and drop, and paste: an image joins the open document as a new layer. */
export async function acceptDroppedFile(file: File): Promise<void> {
  if (isBrushFile(file)) {
    await importBrushFile(file);
    return;
  }
  if (isPsdFile(file)) {
    await openPsdAsDocument(file);
    return;
  }
  if (isPdfFile(file)) {
    await openPdfAsDocument(file);
    return;
  }
  if (isStyleFile(file)) {
    await importStyleFile(file);
    return;
  }
  if (useAppStore.getState().activeDocumentId) await placeImageAsLayer(file);
  else await openImageAsDocument(file);
}

/** Applies the first style preset from an .asl or JSON style pack to the active layer. */
export async function importStyleFile(file: File): Promise<void> {
  const store = useAppStore.getState();
  const doc = store.documents.find((entry) => entry.id === store.activeDocumentId);
  if (!doc?.activeLayerId) {
    store.setStatus('Open a document and select a layer to import a style');
    return;
  }
  const layer = findLayerById(doc.layers, doc.activeLayerId);
  if (!layer) return;
  const name = file.name.toLowerCase();
  let effects = null;
  if (name.endsWith('.asl')) {
    const styles = parseAsl(await file.arrayBuffer());
    effects = styles[0] ?? null;
  } else {
    effects = parseStyleJson(await file.text());
  }
  if (!effects) {
    store.setStatus('No layer styles found in that file');
    return;
  }
  applyLayerEffectsPreset(doc.activeLayerId, effects);
  store.setStatus(`Applied style from ${file.name}`);
}
